import {
  Paper as MuiPaper,
  PaperTypeMap as MuiPaperTypeMap,
  useThemeProps,
} from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';
import * as React from 'react';
import { useUnifyElevationProp } from 'src/styles/elevationUtils';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';

export interface PaperOwnProps {
  /**
   * Unify system elevation level that determines shadow depth when `variant` is not set to `outlined`.
   * Accepts values between 0 and 6 inclusive.
   *
   * Default: 1
   */
  elevation?: number;
}

export type PaperTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiPaperTypeMap['defaultComponent']
> = ExtendTypeMap<
  {
    props: Omit<MuiPaperTypeMap['props'], 'elevation'>;
    defaultComponent: MuiPaperTypeMap['defaultComponent'];
  },
  AdditionalProps & PaperOwnProps,
  RootComponent
>;

export type PaperProps<
  RootComponent extends React.ElementType = MuiPaperTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<PaperTypeMap<AdditionalProps, RootComponent>, RootComponent>;

export const Paper = forwardRefToOverridableComponent<PaperTypeMap, PaperProps>(
  function Paper(inProps, forwardedRef) {
    const props = useThemeProps({ props: inProps, name: 'MuiPaper' });
    const {
      children,
      elevation: unifyElevation = props.variant === 'outlined' ? 0 : 1,
      ...rest
    } = props;
    const muiElevation = useUnifyElevationProp(unifyElevation);

    return (
      <MuiPaper {...rest} ref={forwardedRef} elevation={muiElevation}>
        {children}
      </MuiPaper>
    );
  }
);

export default Paper;
